/**
 * Date utility functions specifically for Failure Tracking components
 * These functions ensure consistent timezone-free date display
 */

/**
 * Get the user's preferred timezone offset in hours
 * This can be configured based on user preference or detected automatically
 * @returns {number} - Timezone offset in hours (e.g., 5.5 for IST, -8 for PST)
 */
function getUserTimezoneOffset() {
  // Option 1: Hardcoded based on user preference (you can make this configurable)
  // For now, defaulting to IST, but this can be made dynamic
  
  // You can implement logic here to:
  // 1. Read from user preferences/settings
  // 2. Detect based on browser timezone
  // 3. Use a configuration setting
  
  // For IST (UTC+5:30)
  const IST_OFFSET = 5.5;
  
  // For PST (UTC-8) - uncomment this line to use PST instead
  // const PST_OFFSET = -8;
  
  // Auto-detect user's timezone (alternative approach)
  // const browserOffset = -new Date().getTimezoneOffset() / 60;
  // return browserOffset;
  
  return IST_OFFSET; // Change this to PST_OFFSET for PST users
}

/**
 * Formats a date string to user's timezone and displays in timezone-free format (MMM DD YYYY)
 * Works for both IST and PST based on getUserTimezoneOffset()
 * @param {string} dateStr - Date string in various formats
 * @returns {string} - Formatted date string like "Jan 28 2025" in user's timezone
 */
export function formatDateTimezoneFreeForFailureTracking(dateStr) {
  if (!dateStr) return "";

  try {
    let dateObj;
    
    // Parse the date string into a Date object
    if (typeof dateStr === 'string') {
      // Handle different input formats
      if (dateStr.includes('T') && (dateStr.endsWith('Z') || dateStr.includes('+') || dateStr.includes('-', 10))) {
        // ISO format with timezone (e.g., "2026-01-27T18:30:00.000Z")
        dateObj = new Date(dateStr);
      } else if (dateStr.includes('T')) {
        // ISO format without timezone
        dateObj = new Date(dateStr + 'Z'); // Treat as UTC
      } else if (dateStr.includes(' ')) {
        // Database format (YYYY-MM-DD HH:MM:SS)
        dateObj = new Date(dateStr + ' UTC'); // Treat as UTC
      } else if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        // Date only format (YYYY-MM-DD)
        dateObj = new Date(dateStr + 'T00:00:00Z'); // Treat as UTC midnight
      } else {
        dateObj = new Date(dateStr);
      }
    } else {
      dateObj = new Date(dateStr);
    }

    if (isNaN(dateObj.getTime())) {
      console.warn("Invalid date in failure tracking:", dateStr);
      return dateStr;
    }

    // Convert to user's timezone (IST, PST, etc.)
    const userOffset = getUserTimezoneOffset();
    const offsetMs = userOffset * 60 * 60 * 1000; // Convert hours to milliseconds
    const userTimezoneDate = new Date(dateObj.getTime() + offsetMs);

    // Extract date components from user's timezone date
    const year = userTimezoneDate.getUTCFullYear();
    const month = userTimezoneDate.getUTCMonth();
    const day = userTimezoneDate.getUTCDate();

    // Month names array
    const monthNames = [
      'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ];
    
    const formattedMonth = monthNames[month];
    const formattedDay = String(day).padStart(2, "0");
    const formattedYear = year;

    return `${formattedMonth} ${formattedDay} ${formattedYear}`;
  } catch (error) {
    console.error("Error formatting failure tracking date:", error, dateStr);
    return dateStr;
  }
}

/**
 * Formats datetime for failure tracking display with user's timezone conversion
 * @param {string} datetimeStr - Datetime string
 * @param {string} type - "date", "time", or "datetime"
 * @returns {string} - Formatted string in user's timezone
 */
export function formatFailureTrackingDateTime(datetimeStr, type = "datetime") {
  if (!datetimeStr) return "";

  try {
    // If only date is requested, use the main formatting function
    if (type === "date") {
      return formatDateTimezoneFreeForFailureTracking(datetimeStr);
    }

    let dateObj;
    
    // Parse the datetime string
    if (typeof datetimeStr === 'string') {
      if (datetimeStr.includes('T') && (datetimeStr.endsWith('Z') || datetimeStr.includes('+') || datetimeStr.includes('-', 10))) {
        dateObj = new Date(datetimeStr);
      } else if (datetimeStr.includes('T')) {
        dateObj = new Date(datetimeStr + 'Z');
      } else if (datetimeStr.includes(' ')) {
        dateObj = new Date(datetimeStr + ' UTC');
      } else {
        dateObj = new Date(datetimeStr + 'T00:00:00Z');
      }
    } else {
      dateObj = new Date(datetimeStr);
    }

    if (isNaN(dateObj.getTime())) {
      console.warn("Invalid datetime in failure tracking:", datetimeStr);
      return datetimeStr;
    }

    // Convert to user's timezone
    const userOffset = getUserTimezoneOffset();
    const offsetMs = userOffset * 60 * 60 * 1000;
    const userTimezoneDate = new Date(dateObj.getTime() + offsetMs);

    // Extract components
    const year = userTimezoneDate.getUTCFullYear();
    const month = String(userTimezoneDate.getUTCMonth() + 1).padStart(2, "0");
    const day = String(userTimezoneDate.getUTCDate()).padStart(2, "0");
    const hours = String(userTimezoneDate.getUTCHours()).padStart(2, "0");
    const minutes = String(userTimezoneDate.getUTCMinutes()).padStart(2, "0");

    const datePart = `${year}-${month}-${day}`;
    const timePart = `${hours}:${minutes}`;

    if (type === "time") return timePart;
    return `${datePart} ${timePart}`;
  } catch (err) {
    console.error("Error formatting failure tracking datetime:", err, datetimeStr);
    return datetimeStr;
  }
}

/**
 * Converts date to input format for failure tracking forms (user's timezone converted)
 * @param {string} dateStr - Date string in various formats
 * @returns {string} - Date in YYYY-MM-DD format for input fields (user's timezone)
 */
export function toFailureTrackingInputFormat(dateStr) {
  if (!dateStr) return "";

  try {
    let dateObj;
    
    // Parse the date string
    if (typeof dateStr === 'string') {
      if (dateStr.includes('T') && (dateStr.endsWith('Z') || dateStr.includes('+') || dateStr.includes('-', 10))) {
        dateObj = new Date(dateStr);
      } else if (dateStr.includes('T')) {
        dateObj = new Date(dateStr + 'Z');
      } else if (dateStr.includes(' ')) {
        dateObj = new Date(dateStr + ' UTC');
      } else if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        // If already in YYYY-MM-DD format, check if we need timezone conversion
        dateObj = new Date(dateStr + 'T00:00:00Z');
      } else {
        dateObj = new Date(dateStr);
      }
    } else {
      dateObj = new Date(dateStr);
    }

    if (isNaN(dateObj.getTime())) {
      return dateStr;
    }

    // Convert to user's timezone
    const userOffset = getUserTimezoneOffset();
    const offsetMs = userOffset * 60 * 60 * 1000;
    const userTimezoneDate = new Date(dateObj.getTime() + offsetMs);

    // Format as YYYY-MM-DD
    const year = userTimezoneDate.getUTCFullYear();
    const month = String(userTimezoneDate.getUTCMonth() + 1).padStart(2, "0");
    const day = String(userTimezoneDate.getUTCDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  } catch (error) {
    console.error("Error converting to failure tracking input format:", error, dateStr);
    return dateStr;
  }
}

/**
 * Test function to verify timezone conversion - can be called from browser console
 * Tests both IST and PST conversions
 */
export function testTimezoneConversion() {
  const testDate = "2026-01-27T18:30:00.000Z";
  console.log("Testing timezone conversion:");
  console.log("Input UTC date:", testDate);
  
  // Test current setting
  console.log("\n=== Current Setting ===");
  console.log("Current timezone offset:", getUserTimezoneOffset(), "hours");
  console.log("Display format:", formatDateTimezoneFreeForFailureTracking(testDate));
  console.log("Input format:", toFailureTrackingInputFormat(testDate));
  console.log("Clean format:", cleanFailureTrackingDate(testDate));
  
  // Manual calculations for verification
  const date = new Date(testDate);
  
  console.log("\n=== Manual Calculations ===");
  // IST calculation (UTC + 5.5)
  const istOffset = 5.5 * 60 * 60 * 1000;
  const istDate = new Date(date.getTime() + istOffset);
  console.log("IST result:", istDate.toISOString(), "→", istDate.toISOString().split('T')[0]);
  
  // PST calculation (UTC - 8)
  const pstOffset = -8 * 60 * 60 * 1000;
  const pstDate = new Date(date.getTime() + pstOffset);
  console.log("PST result:", pstDate.toISOString(), "→", pstDate.toISOString().split('T')[0]);
  
  console.log("\n=== Expected Results ===");
  console.log("For IST users: Should show Jan 28 2025 (2025-01-28)");
  console.log("For PST users: Should show Jan 27 2025 (2025-01-27)");
  
  return "Test completed - check console output";
}

// Make it available globally for testing
if (typeof window !== 'undefined') {
  window.testTimezoneConversion = testTimezoneConversion;
}

/**
 * Clean date string for failure tracking - converts to IST and returns YYYY-MM-DD format
 * @param {string} dateStr - Date string
 * @returns {string} - Clean date string in YYYY-MM-DD format (IST)
 */
export function cleanFailureTrackingDate(dateStr) {
  if (!dateStr) return "";
  if (typeof dateStr !== "string") return "";

  try {
    let dateObj;
    
    // Parse the date string
    if (dateStr.includes('T') && (dateStr.endsWith('Z') || dateStr.includes('+') || dateStr.includes('-', 10))) {
      dateObj = new Date(dateStr);
    } else if (dateStr.includes('T')) {
      dateObj = new Date(dateStr + 'Z');
    } else if (dateStr.includes(' ')) {
      dateObj = new Date(dateStr + ' UTC');
    } else if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      // Already in YYYY-MM-DD format, but might need timezone conversion
      dateObj = new Date(dateStr + 'T00:00:00Z');
    } else {
      dateObj = new Date(dateStr);
    }

    if (isNaN(dateObj.getTime())) {
      // If parsing fails, try to extract date part directly
      if (dateStr.includes("T")) {
        return dateStr.split("T")[0];
      }
      if (dateStr.includes(" ")) {
        return dateStr.split(" ")[0];
      }
      return dateStr;
    }

    // Convert to user's timezone
    const userOffset = getUserTimezoneOffset();
    const offsetMs = userOffset * 60 * 60 * 1000;
    const userTimezoneDate = new Date(dateObj.getTime() + offsetMs);

    // Format as YYYY-MM-DD
    const year = userTimezoneDate.getUTCFullYear();
    const month = String(userTimezoneDate.getUTCMonth() + 1).padStart(2, "0");
    const day = String(userTimezoneDate.getUTCDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  } catch (error) {
    console.error("Error cleaning failure tracking date:", error, dateStr);
    // Fallback to simple extraction
    if (dateStr.includes("T")) {
      return dateStr.split("T")[0];
    }
    if (dateStr.includes(" ")) {
      return dateStr.split(" ")[0];
    }
    return dateStr;
  }
}